/**
 * Interactive Video Carousel
 * Scroll-snap carousel: arrows, optional center-focus highlight (5 cols).
 * Locks carousel min-height to featured card size to avoid vertical jump.
 */

class InteractiveVideoCarousel {
  /**
   * @param {HTMLElement} root - Section root ([data-interactive-video-carousel])
   */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.carousel = root.querySelector('.ivc__carousel');
    this.prevBtn = root.querySelector('[data-carousel-prev]');
    this.nextBtn = root.querySelector('[data-carousel-next]');
    this.slides = Array.from(this.track.children);
    this.highlightCenter = root.dataset.highlightCenter === 'true';
    this._featuredSlide = null;
    this._rafId = 0;
    this._heightRafId = 0;

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);

    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this.track);
    if (this.carousel) {
      this._resizeObserver.observe(this.carousel);
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this._scrollBy(-1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this._scrollBy(1));
    }

    this._updateState();
    this._updateFeatured();
    this._lockMinHeight();
  }

  /** Scroll the track by +1 or -1 slides. */
  _scrollBy(direction) {
    const slideWidth = this._getSlideWidth();
    if (!slideWidth) return;
    this.track.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
  }

  /** Width of a single slide (includes gap). Prefer non-featured for stable step. */
  _getSlideWidth() {
    const sample =
      this.slides.find((slide) => !slide.classList.contains('is-featured')) ||
      this.slides[0];
    if (!sample) return 0;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return sample.offsetWidth + gap;
  }

  _onScroll() {
    this._updateState();
    this._scheduleFeaturedUpdate();
  }

  _onResize() {
    this._updateState();
    this._updateFeatured();
    this._lockMinHeight();
  }

  _scheduleFeaturedUpdate() {
    if (!this.highlightCenter) return;
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = 0;
      this._updateFeatured();
    });
  }

  /** True when center-focus is active (5 cols desktop). */
  _canHighlightCenter() {
    if (!this.highlightCenter) return false;
    if (window.matchMedia('(max-width: 1399px)').matches) return false;
    const perRow = parseFloat(
      getComputedStyle(this.root).getPropertyValue('--vc-per-row')
    );
    return perRow >= 5;
  }

  /**
   * Reserve carousel height = featured card (media + gap + bar).
   * Uses track width math so mid-transition widths cannot shrink the section.
   */
  _lockMinHeight() {
    if (!this.carousel) return;

    if (!this._canHighlightCenter()) {
      this.carousel.style.removeProperty('--ivc-lock-height');
      return;
    }

    const trackWidth = this.track.clientWidth;
    if (!trackWidth) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const featuredWidth = ((trackWidth - 4 * gap) / 5.3) * 1.3;
    const aspect =
      parseFloat(
        getComputedStyle(this.root).getPropertyValue('--pvp-aspect-ratio')
      ) || 0.5625;
    const mediaHeight = featuredWidth / aspect;

    const bar = this.slides[0]?.querySelector('.vcc__bar');
    const stack = this.slides[0]?.querySelector('.pvp--carousel');
    const stackGap = stack
      ? parseFloat(getComputedStyle(stack).rowGap || getComputedStyle(stack).gap) ||
        10
      : 10;
    const barHeight = bar ? bar.getBoundingClientRect().height : 0;

    const lockHeight = Math.ceil(mediaHeight + stackGap + barHeight);
    this.carousel.style.setProperty('--ivc-lock-height', `${lockHeight}px`);
  }

  /** Mark the slide closest to the track's visual center. */
  _updateFeatured() {
    if (!this.highlightCenter) return;

    if (!this._canHighlightCenter()) {
      if (this._featuredSlide) {
        this._featuredSlide.classList.remove('is-featured');
        this._featuredSlide = null;
      }
      for (const slide of this.slides) {
        slide.classList.remove('is-featured');
      }
      this._lockMinHeight();
      return;
    }

    const trackRect = this.track.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;
    let best = null;
    let bestDist = Infinity;

    for (const slide of this.slides) {
      const rect = slide.getBoundingClientRect();
      if (rect.width === 0) continue;
      const slideCenter = rect.left + rect.width / 2;
      const dist = Math.abs(slideCenter - centerX);
      if (dist < bestDist) {
        bestDist = dist;
        best = slide;
      }
    }

    if (!best) return;

    if (best !== this._featuredSlide) {
      if (this._featuredSlide) {
        this._featuredSlide.classList.remove('is-featured');
      }
      best.classList.add('is-featured');
      this._featuredSlide = best;
    }

    this._lockMinHeight();
  }

  _updateState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.hidden = atStart;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.hidden = atEnd;
    }
  }

  destroy() {
    this.track.removeEventListener('scroll', this._onScroll);
    this._resizeObserver.disconnect();
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._heightRafId) cancelAnimationFrame(this._heightRafId);
    if (this.carousel) {
      this.carousel.style.removeProperty('--ivc-lock-height');
    }
  }
}

function initCarousels(scope = document) {
  scope.querySelectorAll('[data-interactive-video-carousel]').forEach((root) => {
    if (root._ivcInstance) {
      root._ivcInstance.destroy();
    }
    root._ivcInstance = new InteractiveVideoCarousel(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCarousels());
} else {
  initCarousels();
}

document.addEventListener('shopify:section:load', (event) => {
  initCarousels(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-interactive-video-carousel]').forEach((root) => {
    if (root._ivcInstance) {
      root._ivcInstance.destroy();
      delete root._ivcInstance;
    }
  });
});

document.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest('[data-interactive-video-carousel]');
  if (root) initCarousels(root.parentElement || document);
});
